import axios from 'axios';

// API configuration from environment variables with generic fallbacks
const CONTENT_TYPE = process.env.REACT_APP_CONTENT_TYPE || 'application/json';
const AUTH_TOKEN_NAME = process.env.REACT_APP_AUTH_TOKEN_NAME || 'authKey';
const CSRF_TOKEN_NAME = process.env.REACT_APP_CSRF_TOKEN_NAME || 'securityKey';
const WITH_CREDENTIALS = process.env.REACT_APP_WITH_CREDENTIALS !== 'false';

// SECURITY NOTE: anything in a REACT_APP_* variable is embedded in the public
// JS bundle, so this key is visible to every visitor and must NOT be treated
// as a secret by the backend. Authorization should rely on the JWT + server-side
// role checks alone. This header is only kept for compatibility while the
// backend still expects it — once the backend drops the X-API-Key requirement,
// delete REACT_APP_ADMIN_API_KEY from the env files and remove this code path.
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY || '';

const IS_DEV = process.env.NODE_ENV === 'development';

const apiClient = axios.create({
  // Relative path: CRA proxy in development, Firebase Hosting rewrite in production
  baseURL: '/api',
  headers: {
    'Content-Type': CONTENT_TYPE,
  },
  withCredentials: WITH_CREDENTIALS,
});

// Request interceptor — attaches auth token, legacy API key, and CSRF token
apiClient.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem(AUTH_TOKEN_NAME);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const isAdminRoute =
      config.url &&
      (config.url.includes('/auth/users') ||
        config.url.includes('/customers') ||
        config.url.includes('/admin/'));

    if (isAdminRoute && ADMIN_API_KEY) {
      config.headers['X-API-Key'] = ADMIN_API_KEY;
    }

    // Attach CSRF token for mutating requests, fetching one if we don't have it yet
    if (config.method !== 'get') {
      let csrfToken = localStorage.getItem(CSRF_TOKEN_NAME);
      if (!csrfToken) {
        try {
          const response = await apiClient.get('/auth/csrf-token');
          csrfToken = response.data.csrfToken;
          localStorage.setItem(CSRF_TOKEN_NAME, csrfToken);
        } catch (error) {
          if (IS_DEV) console.error('Failed to fetch CSRF token:', error.message);
        }
      }
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — CSRF refresh + auth failure handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    if (IS_DEV) {
      console.error('API error:', {
        url: originalRequest.url,
        method: originalRequest.method,
        status: error.response?.status,
        message: error.response?.data?.error || error.message,
      });
    }

    // Retry once with a fresh CSRF token if the server rejected ours
    const isCsrfError =
      error.response?.status === 403 &&
      typeof error.response?.data?.error === 'string' &&
      error.response.data.error.includes('CSRF token');

    if (isCsrfError && !originalRequest._csrfRetried) {
      try {
        const response = await apiClient.get('/auth/csrf-token', {
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        });
        const newToken = response.data.csrfToken;
        localStorage.setItem(CSRF_TOKEN_NAME, newToken);

        originalRequest._csrfRetried = true;
        originalRequest.headers['X-CSRF-Token'] = newToken;
        return apiClient(originalRequest);
      } catch (tokenError) {
        if (IS_DEV) console.error('Failed to refresh CSRF token:', tokenError.message);
      }
    }

    // Expired/invalid session: clear tokens and return to login.
    // Skip this for the login request itself (a wrong password is a 401 too —
    // redirecting would reload the page and wipe the error message).
    const isLoginRequest = originalRequest.url && originalRequest.url.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem(AUTH_TOKEN_NAME);
      localStorage.removeItem(CSRF_TOKEN_NAME);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
